import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  FileCode,
  Plus,
  Search,
  ExternalLink,
  Trash2,
  Eye,
  Rocket,
  Loader2,
  Globe,
} from 'lucide-react'
import { api } from '@/lib/api'
import {
  formatDate,
  formatNumber,
  getStatusColor,
  getStrategyLabel,
  cn,
} from '@/lib/utils'
import toast from 'react-hot-toast'
import { useState } from 'react'

export function SitesPage() {
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.getSites(),
  })

  const deployMutation = useMutation({
    mutationFn: (id: string) => api.deploySite(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      toast.success(`Site deployed! ${data.url}`)
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteSite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      toast.success('Site deleted')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const filteredSites = data?.sites?.filter((s) =>
    s.domain_name?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold">Sites</h1>
          <p className="text-muted-foreground">
            Manage your generated sites
          </p>
        </div>
        <Link to="/dashboard/generate" className="btn-primary">
          <Plus className="h-4 w-4" />
          Generate Site
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search sites..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Sites List */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : filteredSites?.length === 0 ? (
        <div className="card text-center py-12">
          <FileCode className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            {search ? 'No sites found' : 'No sites yet'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {search
              ? 'Try a different search term'
              : 'Generate your first site to get started'}
          </p>
          {!search && (
            <Link to="/dashboard/generate" className="btn-primary">
              <Plus className="h-4 w-4" />
              Generate Site
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredSites?.map((site, i) => (
            <motion.div
              key={site.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="card-hover group"
            >
              {/* Preview Header */}
              <div className="relative h-32 -mx-6 -mt-6 mb-4 rounded-t-xl overflow-hidden bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Globe className="h-12 w-12 text-muted-foreground/20" />
                </div>
                <div className="absolute top-3 right-3 flex gap-2">
                  <span
                    className={cn(
                      'badge',
                      `badge-${getStatusColor(site.status)}`
                    )}
                  >
                    {site.status}
                  </span>
                </div>
              </div>

              {/* Site Info */}
              <div className="space-y-3">
                <div>
                  <Link
                    to={`/dashboard/sites/${site.id}`}
                    className="font-semibold hover:text-primary transition-colors block truncate"
                  >
                    {site.domain_name}
                  </Link>
                  <p className="text-sm text-muted-foreground">
                    {getStrategyLabel(site.site_type)} • {formatDate(site.created_at)}
                  </p>
                </div>

                {/* Stats */}
                <div className="flex gap-4 text-sm">
                  <div>
                    <p className="font-semibold">{formatNumber(site.total_visits)}</p>
                    <p className="text-muted-foreground">Visits</p>
                  </div>
                  <div>
                    <p className="font-semibold">{formatNumber(site.total_leads)}</p>
                    <p className="text-muted-foreground">Leads</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-border">
                  <Link
                    to={`/dashboard/sites/${site.id}`}
                    className="btn-ghost btn-sm flex-1"
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </Link>

                  {site.status === 'draft' ? (
                    <button
                      onClick={() => deployMutation.mutate(site.id)}
                      disabled={deployMutation.isPending}
                      className="btn-primary btn-sm flex-1"
                    >
                      {deployMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Rocket className="h-4 w-4" />
                          Deploy
                        </>
                      )}
                    </button>
                  ) : site.deployed_url ? (
                    <a
                      href={site.deployed_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline btn-sm flex-1"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Visit
                    </a>
                  ) : null}

                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this site?')) {
                        deleteMutation.mutate(site.id)
                      }
                    }}
                    className="btn-ghost btn-sm text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}
